import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { format, startOfWeek, addDays, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import { useAvailabilityData } from '../../contexts/AvailabilityDataContext';
import { useAvailabilityUI } from '../../contexts/AvailabilityUIContext';

const CalendarNavigation: React.FC = () => {
  const { state: dataState } = useAvailabilityData();
  const { state: uiState, dispatch: uiDispatch } = useAvailabilityUI();
  const { currentDate, viewMode } = uiState.calendar;

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });

  const handlePrevious = () => {
    const newDate = viewMode === 'week' 
      ? subWeeks(currentDate, 1)
      : subMonths(currentDate, 1);
    
    uiDispatch({ type: 'SET_CURRENT_DATE', payload: newDate });
  };

  const handleNext = () => {
    const newDate = viewMode === 'week' 
      ? addWeeks(currentDate, 1)
      : addMonths(currentDate, 1);
    
    uiDispatch({ type: 'SET_CURRENT_DATE', payload: newDate });
  };

  const handleViewModeChange = (mode: 'week' | 'month' | 'day') => {
    uiDispatch({ type: 'SET_VIEW_MODE', payload: mode });
  };

  const getDateRangeText = () => {
    if (viewMode === 'week') {
      return `${format(weekStart, 'MMM dd')} - ${format(addDays(weekStart, 6), 'MMM dd, yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center space-x-4">
        <button
          onClick={handlePrevious}
          className="p-2 rounded-lg border border-clinic-primary-lighter hover:bg-clinic-cream/50 transition-colors text-clinic-dark"
          disabled={dataState.loading}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        <h2 className="text-lg font-semibold text-clinic-dark min-w-[200px] text-center">
          {getDateRangeText()}
        </h2>

        <button
          onClick={handleNext}
          className="p-2 rounded-lg border border-clinic-primary-lighter hover:bg-clinic-cream/50 transition-colors text-clinic-dark"
          disabled={dataState.loading}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex items-center space-x-2">
        {(['week', 'month'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => handleViewModeChange(mode)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              viewMode === mode
                ? 'bg-clinic-primary text-white'
                : 'text-clinic-dark hover:bg-clinic-cream/50'
            }`}
            disabled={dataState.loading}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CalendarNavigation;